import { Nutrient } from "../nutrient";
import { z } from "zod/v4";
import { zql } from "../../zero/schema";
import type { Transaction } from "@rocicorp/zero";
import { withZeroTransaction } from "../../zero/utils";
import { createInsertSchema } from "../../utils/sql";
import { foodServing } from "../food/food.sql";
import { genericFoodNutrient } from "./generic-food.sql";
import { Unit } from "@corpuscomplete/core/common/unit";
import { Food } from "../food";
import type { Actor } from "@corpuscomplete/core/actor";
import { Icon } from "@corpuscomplete/core/common/icon";
import { accessDenied } from "@corpuscomplete/core/utils";
import { FoodCategory } from "../food-category";

export namespace GenericFood {
  export type Create = z.input<typeof createSchema>;
  const createSchema = z
    .object({
      id: z.uuid(),
      collectionId: z.uuid(),
      visibility: z.enum(["public", "private"]),
      discoverability: z.enum(["listed", "unlisted"]),
      categoryIds: z.array(z.uuid()).optional().default([]),
      iconSlugs: z.array(z.string()).min(1).max(4).nullable().optional(),
    })
    .extend({
      modifiers: Food.modifierSchema.array(),
      name: z.string().min(1).max(255),
      // genericFood: createInsertSchema(genericFood).omit({ id: true }), // No fields besides "id" on genericFood, so omitting for now
      servings: createInsertSchema(foodServing)
        .omit({
          foodId: true,
          foodVersion: true,
          nutrients: true,
          massQuantity: true,
          massUnitKey: true,
          volumeQuantity: true,
          volumeUnitKey: true,
          modifiers: true,
        })
        .extend({
          modifiers: Food.servingModifierSchema.array(),
          mass: z.object({
            unitKey: Unit.keySchema(["mass"]),
            quantity: z.number().positive().max(999_999),
          }),
          volume: z
            .object({
              unitKey: Unit.keySchema(["volume"]),
              quantity: z.number().positive().max(999_999),
            })
            .nullable(),
        })
        .array()
        .min(1),
      // TODO: What should min here be? 4 is meant to require macros, but could just manually check that in refine?
      nutrients: createInsertSchema(genericFoodNutrient)
        .omit({ foodId: true, foodVersion: true })
        .array()
        .min(4),
      external: z
        .object({
          id: z.string(),
          version: z.string(),
          importVersion: z.number().int().positive(),
          name: z.string(),
          variant: z.string().nullable().optional(),
        })
        .optional(),
    });

  export const createFn = async ({
    tx,
    actor,
    args,
  }: {
    tx: Transaction;
    actor: Actor;
    args: Create;
  }) => {
    const {
      servings: servingsInput,
      nutrients,
      external,
      categoryIds,
      ...food
    } = createSchema.parse(args);

    const hasPermission = await Food.canAddToCollection(tx, actor, food.collectionId, "generic");
    if (!hasPermission) {
      accessDenied("You do not have permission to add a food to this collection");
    }

    const [units, nutrientLookup] = await Promise.all([
      tx.run(zql.unit.where("type", "IN", ["mass", "volume"])),
      Nutrient.createIdLookupForKeys(tx),
    ]);
    const nutrientAmounts = Nutrient.nutrientsToAmounts(nutrients, nutrientLookup);

    if (tx.location === "server") {
      await Icon.validateSlugs(tx.dbTransaction.wrappedTransaction, food.iconSlugs);
    }

    const timeNow = Temporal.Now.instant();
    const version = 1;
    await tx.mutate.foodMeta.insert({
      id: food.id,
      type: "generic",
      collectionId: food.collectionId,
      currentVersion: null,
      visibility: food.visibility,
      discoverability: food.discoverability,
      timeCreated: timeNow,
      timeUpdated: timeNow,
    });
    await tx.mutate.food.insert({
      id: food.id,
      version,
      name: food.name,
      modifiers: food.modifiers,
      iconSlugs: food.iconSlugs ?? null,
      timeCreated: timeNow,
    });

    await tx.mutate.genericFood.insert({
      id: food.id,
      version,
    });

    if (external) {
      await tx.mutate.externalFoodIdentity.insert({
        foodId: food.id,
        collectionId: food.collectionId,
        externalId: external.id,
        externalVariant: external.variant ?? null,
      });
      await tx.mutate.externalFoodProvenance.insert({
        foodId: food.id,
        foodVersion: version,
        collectionId: food.collectionId,
        externalId: external.id,
        externalVersion: external.version,
        importVersion: external.importVersion,
        externalName: external.name,
      });
    }
    await Promise.all(
      nutrients.map(async (nutrient) =>
        tx.mutate.genericFoodNutrient.insert({
          ...nutrient,
          foodId: food.id,
          foodVersion: version,
        }),
      ),
    );

    const servings: Food.ServingInput[] = servingsInput.map((serving) => {
      // Nutrient amounts are per 100g
      const massGrams = Unit.convert(serving.mass, "g").quantity;
      const nutrientScaleFactor = massGrams / 100;
      return {
        ...serving,
        nutrients: Nutrient.scaleAmounts(nutrientAmounts, nutrientScaleFactor),
      };
    });

    Food.assertNoDuplicateUnitServings(servings, units);

    const sortedServings = Food.sortServings(
      Food.addUnitConversionServings(servings, units),
      units,
    );
    await Promise.all(
      sortedServings.map((serving) =>
        tx.mutate.foodServing.insert({
          ...Food.servingInputToRow(serving),
          foodId: food.id,
          foodVersion: version,
        }),
      ),
    );

    if (categoryIds.length > 0) {
      await FoodCategory.addAssociations(tx, {
        foodId: food.id,
        foodVersion: version,
        isACategoryIds: categoryIds,
      });
    }

    await tx.mutate.foodMeta.update({
      id: food.id,
      currentVersion: version,
    });
  };

  export const create = withZeroTransaction(createSchema, ({ tx, ctx, args }) =>
    createFn({ tx, actor: ctx.actor, args }),
  );
}
